import { Loader } from '@myosh/odin-components';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getClusterConfig } from '../helpers/get-cluster-config';
import { useUser } from './user.context';

const fallbackClusterConfig: ClusterConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL!,
  apiKey: process.env.REACT_APP_API_KEY!,
};

interface ClusterConfig {
  apiBaseUrl: string;
  apiKey: string;
}

interface ClusterContextProps {
  children: React.ReactNode;
}

const ClusterContext = createContext<ClusterConfig | undefined>(undefined);

export const ClusterProvider: React.FC<ClusterContextProps> = ({ children }) => {
  const [clusterConfigState, setClusterConfigState] = useState<ClusterConfig>();
  const { state: userState } = useUser();
  const schema = userState?.user?.currentSchema;
  const idToken = userState?.tokens?.id_token;

  useEffect(() => {
    const fetchConfig = () => {
      getClusterConfig(idToken ?? '', schema ?? '')
        .then((response) => {
          const { 'api-base-url': apiBaseUrl, 'api-key': apiKey } = response.data;
          const clusterConfig = apiBaseUrl && apiKey ? { apiBaseUrl, apiKey } : fallbackClusterConfig;
          localStorage.setItem('ClusterConfig', JSON.stringify(clusterConfig));
          setClusterConfigState(clusterConfig);
        })
        .catch(() => {
          localStorage.setItem('ClusterConfig', JSON.stringify(fallbackClusterConfig));
          setClusterConfigState(fallbackClusterConfig);
        });
    };

    fetchConfig();
  }, []);

  return (
    <ClusterContext.Provider value={clusterConfigState}>
      {clusterConfigState ? (
        children
      ) : (
        <div className="mx-auto flex h-screen w-full flex-col items-center justify-center">
          <Loader title="Loading configuration" />
        </div>
      )}
    </ClusterContext.Provider>
  );
};

// context consumer hook
export const useCluster = () => {
  const context = useContext(ClusterContext);

  if (context === undefined) {
    throw new Error('useCluster must be used within ClusterProvider');
  }

  return context;
};
